import { call, put, all, takeEvery } from 'redux-saga/effects'

import {
  signInService,
  getCurrentUser,
  signOutService,
  resetPasswordService,
  createPasswordService,
  createNewPasswordService
} from '../services/cognito'
import {
  SUBMIT_LOGIN,
  submitLoginSuccess,
  submitLoginFailure,
  CHECK_AUTH_STATUS,
  checkAuthStatusSuccess,
  checkAuthStatusFailure,
  SUBMIT_LOGOUT,
  submitLogoutSuccess,
  submitLogoutFailure,
  RESET_PASSWORD,
  resetPasswordSuccess,
  resetPasswordFailure,
  CREATE_PASSWORD,
  createPasswordSuccess,
  createPasswordFailure,
  CREATE_NEW_PASSWORD,
  createNewPasswordSuccess,
  createNewPasswordFailure
} from '../actions/cognito'
import { setLocalStorage } from '../../helpers/storage'
import { getBanners, getCurrentUserProfile } from '../actions/app'

import { redirectTo } from '../../helpers/url'
import { showToasts } from '../../helpers/toasts'
import { ROUTES } from '../../constants/routes'
import { TOAST_TYPE } from '../../constants/other'
import { t } from 'i18next'

function* cognitoWatcher() {
  yield all([
    // PLOP_APPEND_PATTERN_ANCHOR_WATCHER
    takeEvery(SUBMIT_LOGIN, submitLoginWorker),
    takeEvery(CHECK_AUTH_STATUS, checkAuthWorker),
    takeEvery(SUBMIT_LOGOUT, submitLogoutWorker),
    takeEvery(CREATE_PASSWORD, createPasswordWorker),
    takeEvery(CREATE_NEW_PASSWORD, createNewPasswordWorker),
    takeEvery(RESET_PASSWORD, resetPasswordWorker)
  ])
}

// PLOP_APPEND_PATTERN_ANCHOR_WORKER

function* submitLoginWorker({ userCredentials }) {
  try {
    const response = yield call(signInService, userCredentials)

    if (response.isSignedIn) {
      yield put(submitLoginSuccess(response))
      yield put(getCurrentUserProfile())

      // we need to reload banners on success submit
      yield put(getBanners())
    }
  } catch (e) {
    // code is added as key for parsing errors inside the ProgressButton
    yield put(submitLoginFailure({ ...e, code: 'cognito_error' }))
  }
}

function* checkAuthWorker() {
  try {
    const response = yield call(getCurrentUser)
    if (response?.userId) {
      yield put(checkAuthStatusSuccess(response))
      yield put(getCurrentUserProfile())
    }
  } catch (e) {
    // save the destination URL which could be used for redirecting after Login
    // save full url with params to avoid missing opened forms or modals
    // this localStorage property used in Routes and is cleaned up after redirection
    setLocalStorage('landingUrl', window.location.pathname + window.location.search)
    // code is added as key for parsing errors inside the ProgressButton
    yield put(checkAuthStatusFailure({ ...e, code: 'cognito_error' }))
  }
}

function* submitLogoutWorker() {
  try {
    const response = yield call(signOutService)
    yield put(submitLogoutSuccess(response))
  } catch (e) {
    // code is added as key for parsing errors inside the ProgressButton
    yield put(submitLogoutFailure({ ...e, code: 'cognito_error' }))
  }
}

function* createPasswordWorker({ data }) {
  try {
    // in current App version we don't have a way to create a user so the code below is not tested after aws-apmlify
    // was updated and all code was refactored due to new version
    const response = yield call(createPasswordService, data)
    yield put(createPasswordSuccess(response))

    if (response.signInUserSession) {
      yield put(submitLoginSuccess(response))
      yield put(getCurrentUserProfile())
    }
  } catch (e) {
    // code is added as key for parsing errors inside the ProgressButton
    yield put(createPasswordFailure({ ...e, code: 'cognito_error' }))
  }
}

function* createNewPasswordWorker({ data }) {
  try {
    yield call(createNewPasswordService, data)
    yield put(createNewPasswordSuccess({ wasCreated: true }))

    showToasts({
      type: TOAST_TYPE.success,
      message: 'Password was created, please login.'
    })
    redirectTo(ROUTES.login)
  } catch (e) {
    // code is added as key for parsing errors inside the ProgressButton
    yield put(createNewPasswordFailure({ ...e, code: 'cognito_error' }))
  }
}

function* resetPasswordWorker({ email, showSuccessToast }) {
  try {
    const response = yield call(resetPasswordService, email)

    yield put(resetPasswordSuccess(response))

    if (showSuccessToast) {
      showToasts({
        type: TOAST_TYPE.success,
        message: t('The reset password email has been resent. Please check your junk folder.')
      })
    }
  } catch (e) {
    // code is added as key for parsing errors inside the ProgressButton
    yield put(resetPasswordFailure({ ...e, code: 'cognito_error' }))
  }
}

export default cognitoWatcher
